import { Icon, Intent } from "@blueprintjs/core";
import axios from "axios";
import classNames from "classnames";
import { Button, ButtonSave, LinkButton } from "components/buttons";
import { DataDisplayRecord } from "components/data-display";
import { DateToLocal } from "components/dates";
import { FormDateInput, FormHelperText, FormNumericInput, FormSelect, FormSuggest, FormTextInput } from "components/form-fields";
import { FormDragAndDropFileUploader, FormFileUploadStatus } from "components/form-fields/FormDragAndDropFileUploader";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { MediaList } from "components/media";
import { NotesThread } from "components/note";
import { Breadcrumbs, Tabs } from "components/navigation";
import { AlertConfirm, AlertUnsavedData, Modal, ModalSize, NotificationInline, NotificationToaster } from "components/notifications";
import { ActionPanel } from "components/panel";
import { useValidation } from "hooks/useValidation";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AccountService, AssetService, GlobalSearchService, LocationService } from "services";
import { PcnService } from "services/PcnService";
import { useDirtyData } from "hooks/useDirtyData";
import { PageRestricted } from "pages/errors/page-restricted";
import { UserAccess } from "components/user-access";

export function PcnAdminEdit({ breadcrumbs }) {

    const { id } = useParams();
    var navigate = useNavigate();

    const pcnFeatureId = 3;
    const validationError = 400;
    const defaultPageSize = 50;
    const requiredAction = "Internal";

    const [onHold, setOnHold] = useState(true);
    const [isValid, errors, validate] = useValidation();

    const [issueTypes, setIssueTypes] = useState([]);
    const [issueAuthorities, setIssueAuthorities] = useState([]);
    const [contraventionCodes, setContraventionCodes] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [existingAssetAccounts, setExistingAssetAccounts] = useState([]);
    const issueInterval13Days = 13;
    const issueInterval20Days = 20;
    const issueInterval27Days = 27;

    const issueFromIntervalDays = [issueInterval13Days, issueInterval20Days, issueInterval27Days];
    const [issueFromIntervalTabs, setIssueFromIntervalTabs] = useState([]);
    const [selectedIssueFromInterval, setSelectedIssueFromInterval] = useState(null);
    const [fullAmountChangedManually, setFullAmountChangedManually] = useState(false);
    const [coreDetails, setCoreDetails] = useState({
        registration: "",
        submittedDate: "",
        account: ""
    })

    const [pcnDetails, setPcnDetails] = useState({});
    const [originalHoldingAreaRequest, setOriginalHoldingAreaRequest] = useState({});
    const [holdingAreaRequest, setHoldingAreaRequest] = useState({
        accountId: null,
        assetId: null,
        issueType: "",
        issueDate: null,
        issuingAuthority: "",
        contraventionDate: null,
        contraventionCode: "",
        contraventionLocation: "",
        datePaymentDue: null,
        amountDue: 0,
        fullAmount: 0
    });

    const [showNewAssetModal, setShowNewAssetModal] = useState(false);
    const [savingNewAsset, setSavingNewAsset] = useState(false);
    const [locationList, setLocationList] = useState([]);
    const locationFilter = {
        "filters":
            [
                { "key": "States", "value": "Active" }
            ]
    };
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedAssetType, setSelectedAssetType] = useState(null);

    const [showDetailsPanel, setShowDetailsPanel] = useState(true);

    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const [savingDetails, setSavingDetails] = useState(false);

    const [filesToUpload, setFilesToUpload] = useState([]);
    const [fileUploadList, setFileUploadList] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState(false);

    const [uploadCompleted, setUploadCompleted] = useState(false);

    const [attachments, setAttchments] = useState([]);

    const [showAuthorityMismatchError, setShowAuthorityMismatchError] = useState(false);

    const [savingNote, setSavingNote] = useState(false);
    const [newNoteDraft, setNewNoteDraft] = useState("");

    const isDataDirty = useDirtyData(originalHoldingAreaRequest, holdingAreaRequest);

    useEffect(() => {
        setIssueFromIntervalTabs(issueFromIntervalDays.map(d => {
            return { title: d.toString(), key: d.toString(), selected: d === selectedIssueFromInterval }
        }));

        if (selectedIssueFromInterval != null && holdingAreaRequest.issueDate != null) {
            onHoldingAreaUpdate({
                datePaymentDue: moment(holdingAreaRequest.issueDate).add(selectedIssueFromInterval, "days").format("YYYY-MM-DD")
            })
        }

    }, [selectedIssueFromInterval]);

    useEffect(() => {

        if (holdingAreaRequest.datePaymentDue == null || holdingAreaRequest.issueDate == null) {
            return;
        }

        const daysUntilPaymentDue = Math.abs(moment(holdingAreaRequest.datePaymentDue).diff(moment(holdingAreaRequest.issueDate), "days"));
        setSelectedIssueFromInterval(issueFromIntervalDays.find(d => d === daysUntilPaymentDue));

    }, [holdingAreaRequest.datePaymentDue, holdingAreaRequest.issueDate]);

    useEffect(() => {

        if (pcnDetails?.pcnNumber == null || holdingAreaRequest.issuingAuthority == null || issueAuthorities == null) {
            setShowAuthorityMismatchError(false);
            return;
        }

        var foundAuthority = issueAuthorities.find(x => x.id === holdingAreaRequest.issuingAuthority);

        setShowAuthorityMismatchError(pcnDetails.pcnNumber.indexOf(foundAuthority?.key ?? "-") !== 0);

    }, [pcnDetails, holdingAreaRequest, issueAuthorities]);

    function init() {

        if (id == null) {
            return;
        }

        //Fetch accounts, options and the PCN
        const onLoadError = function (error) {
            if (error.status === validationError) {
                setOnHold(false);
                setIsLoading(false);
            } else {
                NotificationToaster.show(Intent.DANGER, "Could not fetch PCN details, please try again");
            }
        }

        axios.all([AccountService.getAllAccounts([pcnFeatureId], "name"), PcnService.getOptions(), AssetService.getAssetOptions(), PcnService.getOnHoldPcn(id)]).then(
            axios.spread(function (accountResponse, optionsResponse, assetOptionsResponse, pcnResponse) {

                setPcnDetails(pcnResponse);

                const mappedRequest = {
                    accountId: pcnResponse?.account?.id,
                    assetId: pcnResponse?.asset?.id,
                    issueType: pcnResponse?.issue?.type,
                    issueDate: pcnResponse?.issue?.date,
                    issuingAuthority: pcnResponse?.issue?.authority,
                    contraventionDate: pcnResponse?.contravention?.dateTime,
                    contraventionCode: pcnResponse?.contravention?.code,
                    contraventionLocation: pcnResponse?.contravention?.location,
                    datePaymentDue: pcnResponse?.payment?.dateDue,
                    amountDue: pcnResponse?.payment?.amountDue,
                    fullAmount: pcnResponse?.payment?.fullAmount
                }
                setHoldingAreaRequest(mappedRequest);
                setOriginalHoldingAreaRequest(mappedRequest);

                setFullAmountChangedManually(pcnResponse?.payment?.fullAmount > 0 && pcnResponse?.payment?.fullAmount != null);

                setCoreDetails((prev) => {
                    return {
                        ...prev,
                        registration: pcnResponse?.registrationOnPcn,
                        submittedDate: pcnResponse?.receivedDateTime
                    }
                });

                setAssetTypes(assetOptionsResponse.assetTypes?.filter(x => x.visOnly === false && x.publicAllowed === true)?.map((assetType) => {
                    return {
                        id: assetType.id,
                        name: assetType.name
                    }
                }) ?? []);

                const nameSorter = (a, b) => a.name.localeCompare(b.name);
                const keySorter = (a, b) => a.key.localeCompare(b.key);

                setAccounts(accountResponse.data.filter(a => a != null).map(a => ({ id: a.id, name: `${a.name} - ${a.memberNumber}` })).sort(nameSorter));
                setIssueTypes(optionsResponse.issueTypes.sort(nameSorter).map(i => ({ id: i.key, name: i.name })));
                setIssueAuthorities(optionsResponse.issuingAuthorities.map(i => ({ id: i.key, key: i.code, name: `${i.code} - ${i.name}` })).sort(nameSorter));
                setContraventionCodes(optionsResponse.contraventionCodes.sort(keySorter).map(c => ({ id: c.key, name: `${c.key} - ${c.name}` })));

                setShowDetailsPanel((pcnResponse?.missingFields?.filter(x => x !== "attachments")?.length ?? 0) > 0)

                GlobalSearchService.search("asset", defaultPageSize, 1, pcnResponse?.registrationOnPcn).then((assetResponse) => {
                    if (assetResponse.data != null) {
                        var foundRecord = assetResponse.data.find(x => x.accountId === pcnResponse?.account?.id);

                        if (foundRecord) {
                            onHoldingAreaUpdate({ assetId: foundRecord.entityId });
                        }

                        setExistingAssetAccounts(assetResponse.data.map((asset) => {
                            return { id: asset.entityId, name: asset.registration, account: asset.accountId }
                        }))

                    }
                }).finally(() => {
                    setIsLoading(false);
                })

            }),
            onLoadError
        ).catch(onLoadError);

    }

    useEffect(init, [id]);

    useEffect(() => {
        if (pcnDetails?.attachments == null) {
            return;
        }

        setAttchments(pcnDetails.attachments.map((attachment) => {
            return {
                fileName: attachment.filename,
                url: attachment.url
            }
        }));
    }, [pcnDetails.attachments]);

    function onHoldingAreaUpdate(updates) {

        setHoldingAreaRequest((prev) => {
            return {
                ...prev,
                ...updates
            };
        })
    }

    useEffect(validateForm, [holdingAreaRequest])

    function validateForm() {
        let rules = [];

        rules.push({ fieldName: "accountId", required: true });
        rules.push({ fieldName: "assetId", required: true });
        rules.push({ fieldName: "issueType", required: true });
        rules.push({ fieldName: "issueDate", required: true, type: "date", minValue: holdingAreaRequest.contraventionDate == null ? null : moment(holdingAreaRequest.contraventionDate).toDate() });
        rules.push({ fieldName: "issuingAuthority", required: true });
        rules.push({ fieldName: "contraventionDate", required: true, type: "dateTime" });
        rules.push({ fieldName: "contraventionCode", required: true });
        rules.push({ fieldName: "contraventionLocation", required: true });
        rules.push({ fieldName: "datePaymentDue", required: true, type: "date", minValue: holdingAreaRequest.contraventionDate == null ? null : moment(holdingAreaRequest.contraventionDate).add(1, 'days').toDate() });
        rules.push({ fieldName: "amountDue", required: true, type: "number", minValue: 1 });
        rules.push({ fieldName: "fullAmount", required: true, type: "number", minValue: holdingAreaRequest.amountDue ?? 1 });

        validate(rules, holdingAreaRequest);

    }

    function onPaymentDateFromNumberClick(index) {
        setSelectedIssueFromInterval(issueFromIntervalDays[index]);
    }

    function onAmountDueChanged(item) {

        var fullAmount = holdingAreaRequest.fullAmount
        if (!fullAmountChangedManually && !isNaN(item)) {
            fullAmount = item * 2;
        }

        onHoldingAreaUpdate({ amountDue: item, fullAmount })
    }

    function onAccountChange(item) {

        //Try and see if the vehicle is on the account
        const linkedAsset = existingAssetAccounts.find(a => a.account === item.id);
        onHoldingAreaUpdate({ accountId: item.id, assetId: linkedAsset ? linkedAsset.id : null });
        setLocationList([]);
        setCoreDetails((prev) => {
            return {
                ...prev,
                account: item?.name ?? "",
            }
        })

    }

    function onNewAssetClose() {
        setShowNewAssetModal(false);
        setSelectedLocation(null);
        setSelectedAssetType(null);
    }

    function onNewAssetOpen() {
        setShowNewAssetModal(true);
    }

    function onCreateNewAsset() {

        setSavingNewAsset(true);

        var assetRequest = {
            accountId: holdingAreaRequest.accountId,
            locationId: selectedLocation?.id,
            registration: pcnDetails?.registrationOnPcn,
            assetTypeId: selectedAssetType?.id
        };

        PcnService.addAsset(assetRequest).then(function (newAssetId) {

            setExistingAssetAccounts((prev) => [...prev, { id: newAssetId, account: holdingAreaRequest.accountId }]);
            NotificationToaster.show(Intent.SUCCESS, "New asset created and linked to PCN");
            onNewAssetClose();
            setSavingNewAsset(false);
            onHoldingAreaUpdate({ assetId: newAssetId });

        }, function (error) {

            if (error.response.status === validationError) {
                NotificationToaster.show(Intent.WARNING, error.response.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Could not create the new asset, please try again");
            }

            setSavingNewAsset(false);

        });
    }

    function searchForLocation(searchTerm) {
        LocationService.getLocationListForAnotherAccount(holdingAreaRequest.accountId, defaultPageSize, 1, searchTerm, "", "", locationFilter).then((locationResponse) => {
            setLocationList(locationResponse.data.map(function (d) {
                return {
                    id: d.id,
                    name: d.name
                }
            }));
        }, function () {
            NotificationToaster.show(Intent.WARNING, "Could not search for a location, please try agian.");
            setLocationList([]);
        });
    }

    function onLocationSelect(item) {
        setSelectedLocation(item);
    }

    function onAssetTypeSelect(item) {
        setSelectedAssetType(item);
    }

    function onFileSelection(files) {
        setFilesToUpload((prev) => [
            ...prev,
            ...files
        ]);
        setFileUploadList((prev) => [
            ...prev,
            ...files.map((file) => {
                return {
                    name: file.name,
                    size: file.size,
                    status: FormFileUploadStatus.PENDING
                }
            })
        ])
    }

    function onFileRemove(filename) {

        setFilesToUpload((prev) => [
            ...prev
        ].filter((f) => f.name !== filename));

        setFileUploadList((prev) => [
            ...prev
        ].filter((f) => f.name !== filename));
    }

    function onUploadFilesBegin() {
        setUploadingFiles(true);

        let uploadQueue = [];

        filesToUpload.forEach((file) => {
            uploadQueue.push(uploadFile(file));
        })

        setFileUploadList((prev) => {
            return prev.map((file) => {
                return {
                    ...file,
                    status: FormFileUploadStatus.UPLOADING
                }
            });
        });

        Promise.all(uploadQueue).then(function (values) {

            if (!values.some(result => result === true)) {
                NotificationToaster.show(Intent.DANGER, "None of the attachments could be uploaded");
                return;
            } else if (values.some(result => result === false)) {
                NotificationToaster.show(Intent.WARNING, "Some of the attachment could not be uploaded");
            } else {
                NotificationToaster.show(Intent.SUCCESS, "Attachments uploaded successfully");
            }

            setUploadCompleted(true);
            PcnService.getPcn(id).then((pcnResponse) => {
                setPcnDetails((prev) => {
                    return {
                        ...prev,
                        attachments: pcnResponse.attachments
                    }
                })
            })

        }, function () {
            NotificationToaster.show(Intent.DANGER, "Could not upload the attachments, please try again");
        }).finally(function () {
            setUploadingFiles(false);

            setFileUploadList((prev) => [
                ...prev
            ].filter(x => x.status !== FormFileUploadStatus.COMPLETE));

        });

    }

    useEffect(() => {
        setFilesToUpload((prev) => {
            return [...prev].filter((f) => fileUploadList.some((fu) => fu.name === f.name));
        })
    }, [fileUploadList])

    function uploadFile(file) {
        return new Promise((resolve) => {

            PcnService.uploadAttachment(id, file).then(function () {
                setFileUploadList((prev) => {
                    var clonedList = [...prev];
                    var uploadedFile = clonedList.find(x => x.name === file.name);

                    if (uploadedFile != null) {
                        uploadedFile.status = FormFileUploadStatus.COMPLETE
                    }
                    return clonedList;
                });

                resolve(true);
            }, function (error) {

                setFileUploadList((prev) => {
                    var clonedList = [...prev];
                    var uploadedFile = clonedList.find(x => x.name === file.name);

                    if (uploadedFile != null) {
                        uploadedFile.status = FormFileUploadStatus.FAILED
                        uploadedFile.statusComment = error
                    }
                    return clonedList;
                });
                resolve(false);
            })
        });
    }

    function onDetailsSave() {
        setSavingDetails(true);
        PcnService.updateOnHoldPcn(id, holdingAreaRequest).then(function () {
            setShowDetailsPanel(false);
            NotificationToaster.show(Intent.SUCCESS, "PCN Details updated successfully");
            setOriginalHoldingAreaRequest(holdingAreaRequest);
        }, function (error) {
            if (error.response.status === validationError) {
                NotificationToaster.show(Intent.WARNING, error.response.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "An error occurred while saving the details, please try again");
            }
        }).finally(function () {
            setSavingDetails(false);
        })
    }

    function onDeletePCN() {
        PcnService.deletePcn(id)
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "PCN deleted successfully."
                    );
                    navigate(`/pcn-admin/on-hold`);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "PCN could not be deleted. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "PCN could not be deleted. Please try again."
                );
            });
    }

    function refreshNoteThread() {
        PcnService.getOnHoldPcn(id).then((pcnResponse) => {
            setPcnDetails((prev) => {
                return {
                    ...prev,
                    noteThread: pcnResponse.noteThread
                };
            });
        });
    }

    function onAddNote(noteText) {
        setSavingNote(true);
        PcnService.addPcnNote(id, noteText).then(function () {
            setNewNoteDraft("");
            NotificationToaster.show(Intent.SUCCESS, "Note added successfully");
            refreshNoteThread();
        }, function (error) {
            if (error?.status === validationError) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Could not add the note, please try again");
            }
        }).finally(function () {
            setSavingNote(false);
        });
    }

    function onEditNote(noteId, noteText) {
        setSavingNote(true);
        // The rejection is rethrown so the notes thread stays in edit mode when the save fails
        return PcnService.updatePcnNote(id, noteId, noteText).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Note updated successfully");
            refreshNoteThread();
        }, function (error) {
            if (error?.status === validationError) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Could not update the note, please try again");
            }
            throw error;
        }).finally(function () {
            setSavingNote(false);
        });
    }

    function onDeleteNote(noteId) {
        setSavingNote(true);
        PcnService.deletePcnNote(id, noteId).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Note deleted successfully");
            refreshNoteThread();
        }, function (error) {
            if (error?.status === validationError) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Could not delete the note, please try again");
            }
        }).finally(function () {
            setSavingNote(false);
        });
    }

    // The API returns the thread oldest first; the page shows the newest note at the top
    const noteThreadItems = (pcnDetails?.noteThread ?? []).map((note) => {
        return {
            id: note.pcnNoteId,
            text: note.noteText,
            author: note.createdBy,
            createdDate: note.createdDate,
            modifiedBy: note.modifiedBy,
            modifiedDate: note.modifiedDate,
            canEdit: note.canEdit
        };
    }).sort((a, b) => moment(b.createdDate).valueOf() - moment(a.createdDate).valueOf());

    return (
        <UserAccess perform={requiredAction}
            yes={() => (

                <div className="row spacer-top">
                    <Breadcrumbs items={breadcrumbs} />
                    <ShowHide
                        evaluator={onHold}
                        show={<ShowHide
                            evaluator={(pcnDetails?.attachments?.length ?? 0) > 0 && filesToUpload.length === 0 && !showDetailsPanel}
                            show={(
                                <div>
                                    <div className="spacer-bottom" id="create-success-icon">
                                        <Icon icon="tick-circle" size={40} intent={Intent.SUCCESS} />
                                    </div>
                                    <h2>PCN {pcnDetails?.pcnNumber} has been successfully taken off hold</h2>
                                    <div className="button-row" id="holding-area-success-buttons">
                                        <LinkButton intent="primary" text="PCN Holding Area" id="on-hold-listing" href="/pcn-admin/on-hold" />
                                        <LinkButton intent="primary" text="View PCN" id="pcn-view" externalLink href={`${window.env.PCN_URL}/pcndetail?pcnId=${id}`} />
                                    </div>
                                </div>
                            )}
                            hide={(
                                <>
                                    <h1 className={classNames({ "bp6-skeleton": isLoading })}>PCN - {pcnDetails?.pcnNumber}</h1>
                                    <div className="spacer-bottom">
                                        <Button
                                            id="btn-delete-pcn"
                                            intent={Intent.DANGER}
                                            onClick={() => setConfirmingDelete(true)}
                                            text="Delete PCN"
                                            loading={isLoading}
                                        />
                                    </div>
                                    <div className="spacer-bottom">
                                        <GridLayout gap={40} numberOfColumns={4} className={classNames({ "bp6-skeleton": isLoading })}>
                                            <DataDisplayRecord header="Registration" data={coreDetails.registration} />
                                            <DataDisplayRecord header="Date Submitted" data={<DateToLocal>{coreDetails.submittedDate}</DateToLocal>} />
                                        </GridLayout>
                                    </div>
                                    <NotificationInline intent="warning" show={showAuthorityMismatchError} text="PCN number and the issuing authority do not match, please check these are correct" />
                                    <div className="spacer-bottom">
                                        <ActionPanel loadingData={isLoading} intent={showDetailsPanel ? "warning" : "success"} title={showDetailsPanel ? "Missing Fields" : "PCN Details"}>
                                            <div>
                                                <ShowHide
                                                    evaluator={showDetailsPanel}
                                                    show={(
                                                        <p className="spacer-bottom">
                                                            The auto import process failed to set some of the required fields. Please set the correct values below, once set the PCN will be able to be reviewed like normal.
                                                        </p>
                                                    )}
                                                />

                                                <GridLayout numberOfColumns={3} gap={[0, 30]} customColumnRatios={[1, 2]}>
                                                    <div>
                                                        <FormSelect
                                                            fill
                                                            headingText="Account"
                                                            placeholder="Select Account"
                                                            items={accounts}
                                                            loading={isLoading}
                                                            selectedValue={holdingAreaRequest.accountId}
                                                            onItemSelect={onAccountChange}
                                                            dangerHelperText={errors.accountId}
                                                            disabled={!showDetailsPanel}
                                                        />
                                                    </div>
                                                    <div className="form-field no-margin">
                                                        <h4 className={classNames({ "bp6-skeleton": isLoading })}>Vehicle</h4>
                                                        <ShowHide
                                                            evaluator={holdingAreaRequest.accountId == null}
                                                            show={(
                                                                <div className={classNames("inline-items", { "bp6-skeleton": isLoading })}>
                                                                    <div className="inline-item icon">
                                                                        <Icon icon="history" />
                                                                    </div>
                                                                    <div>Please select an account before selecting a vehicle</div>
                                                                </div>
                                                            )}
                                                            hide={(
                                                                <ShowHide
                                                                    evaluator={holdingAreaRequest.assetId != null}
                                                                    show={(
                                                                        <div className={classNames("inline-items", { "bp6-skeleton": isLoading })}>
                                                                            <div className="inline-item icon">
                                                                                <Icon icon="tick-circle" intent={Intent.SUCCESS} />
                                                                            </div>
                                                                            <div>Vehicle exists on this account and has been linked</div>
                                                                        </div>
                                                                    )}
                                                                    hide={(
                                                                        <div className={classNames("inline-items", { "bp6-skeleton": isLoading })}>
                                                                            <div>
                                                                                <Button large={false} intent="primary" icon="plus" iconOnly onClick={onNewAssetOpen} />
                                                                            </div>
                                                                            <FormHelperText danger>Vehicle does not exist for this account, please add one</FormHelperText>
                                                                        </div>
                                                                    )}
                                                                />
                                                            )}
                                                        />


                                                    </div>
                                                </GridLayout>
                                                <GridLayout numberOfColumns={3} gap={[0, 30]}>
                                                    <div>
                                                        <FormSelect
                                                            fill
                                                            headingText="Issue Type"
                                                            placeholder="Select Issue Type"
                                                            items={issueTypes}
                                                            selectedValue={holdingAreaRequest.issueType}
                                                            onItemSelect={(item) => onHoldingAreaUpdate({ issueType: item.id })}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.issueType}
                                                            disabled={!showDetailsPanel}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormSelect
                                                            fill
                                                            headingText="Issue Authority"
                                                            placeholder="Select Issue Authority"
                                                            items={issueAuthorities}
                                                            selectedValue={holdingAreaRequest.issuingAuthority}
                                                            onItemSelect={(item) => onHoldingAreaUpdate({ issuingAuthority: item.id })}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.issuingAuthority}
                                                            disabled={!showDetailsPanel}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormDateInput
                                                            headingText="Issue Date"
                                                            value={holdingAreaRequest.issueDate}
                                                            fill
                                                            onChange={(item) => onHoldingAreaUpdate({ issueDate: item ? moment(item).format("YYYY-MM-DD") : null })}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.issueDate}
                                                            disabled={!showDetailsPanel}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormSelect
                                                            fill
                                                            headingText="Contravention Code"
                                                            placeholder="Select Contravention Code"
                                                            items={contraventionCodes}
                                                            selectedValue={holdingAreaRequest.contraventionCode}
                                                            onItemSelect={((item) => onHoldingAreaUpdate({ contraventionCode: item.id }))}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.contraventionCode}
                                                            disabled={!showDetailsPanel}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormDateInput
                                                            headingText="Contravention Date/Time"
                                                            value={holdingAreaRequest.contraventionDate}
                                                            includeTime
                                                            fill
                                                            onChange={(item) => onHoldingAreaUpdate({ contraventionDate: item ? moment(item).format("YYYY-MM-DD HH:mm") : null })}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.contraventionDate}
                                                            disabled={!showDetailsPanel}
                                                            maxDate={moment().toDate()}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormTextInput
                                                            headingText="Location of Contravention"
                                                            value={holdingAreaRequest.contraventionLocation}
                                                            fill
                                                            onChange={(item) => onHoldingAreaUpdate({ contraventionLocation: item?.target?.value })}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.contraventionLocation}
                                                            disabled={!showDetailsPanel}
                                                        />
                                                    </div>
                                                    <div className="form-field">
                                                        <h4>Payment Due Date</h4>
                                                        <div className="inline-items">
                                                            <div className="inline-item fill">
                                                                <FormDateInput
                                                                    value={holdingAreaRequest.datePaymentDue}
                                                                    fill
                                                                    onChange={(item) => onHoldingAreaUpdate({ datePaymentDue: item ? moment(item).format("YYYY-MM-DD") : null })}
                                                                    loading={isLoading}
                                                                    disabled={!showDetailsPanel}
                                                                />
                                                            </div>
                                                            <div className="inline-item ">
                                                                <Tabs
                                                                    disabled={holdingAreaRequest.issueDate == null || !showDetailsPanel}
                                                                    className="button-tabs"
                                                                    tabs={issueFromIntervalTabs}
                                                                    singleRow
                                                                    onClick={onPaymentDateFromNumberClick}
                                                                />

                                                            </div>
                                                        </div>
                                                        {errors.datePaymentDue && !isLoading && <FormHelperText danger>{errors.datePaymentDue}</FormHelperText>}
                                                    </div>
                                                    <div>
                                                        <FormNumericInput
                                                            prefixText="£"
                                                            headingText="Amount Due"
                                                            selectedValue={holdingAreaRequest.amountDue}
                                                            fill
                                                            onValueChange={onAmountDueChanged}
                                                            min={1}
                                                            max={9999}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.amountDue}
                                                            disabled={!showDetailsPanel}
                                                            disabledInput={!showDetailsPanel}
                                                            decimalPlaces={2}
                                                            allowDecimal
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormNumericInput
                                                            prefixText="£"
                                                            headingText="Full Amount"
                                                            selectedValue={holdingAreaRequest.fullAmount}
                                                            fill
                                                            onValueChange={(item) => onHoldingAreaUpdate({ fullAmount: item })}
                                                            min={1}
                                                            max={9999}
                                                            loading={isLoading}
                                                            dangerHelperText={errors.fullAmount}
                                                            disabled={!showDetailsPanel}
                                                            disabledInput={!showDetailsPanel}
                                                            decimalPlaces={2}
                                                            allowDecimal
                                                        />
                                                    </div>
                                                </GridLayout>
                                                <ShowHide
                                                    evaluator={showDetailsPanel}
                                                    show={(
                                                        <div>
                                                            <div className="bp6-dialog-footer-actions">
                                                                <ButtonSave
                                                                    onClick={onDetailsSave}
                                                                    disabled={savingDetails}
                                                                    simpleDisabled={!isValid}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                />

                                            </div>
                                        </ActionPanel>
                                    </div>

                                    <div className="spacer-bottom">
                                        <ActionPanel loadingData={isLoading} intent={uploadCompleted ? "success" : "warning"} title="Missing Attachments">
                                            <div>
                                                <ShowHide
                                                    evaluator={(pcnDetails?.attachments?.length ?? 0) === 0}
                                                    show={(
                                                        <p className="spacer-bottom">
                                                            No attachments have been uploaded, please upload at least one attachment to take this PCN off hold
                                                        </p>
                                                    )}
                                                />
                                                <FormDragAndDropFileUploader
                                                    showVersions={false}
                                                    maxFileSize={2097152}
                                                    allowedFileTypes={["image/png", "image/gif", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel", "", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/pdf", "text/csv", "image/jpeg", "image/jpg"]}
                                                    onChange={onFileSelection}
                                                    onRemoveDocument={onFileRemove}
                                                    documents={fileUploadList}
                                                    fixedTableHeight={false}
                                                    disabled={uploadingFiles}
                                                />
                                                <div>
                                                    <div className="bp6-dialog-footer-actions">
                                                        <ButtonSave
                                                            onClick={onUploadFilesBegin}
                                                            disabled={uploadingFiles}
                                                            simpleDisabled={filesToUpload.length === 0}
                                                            text="Upload"
                                                        />
                                                    </div>
                                                </div>

                                            </div>


                                        </ActionPanel>
                                    </div>


                                    <ShowHide
                                        evaluator={(pcnDetails?.attachments?.length ?? 0) > 0}
                                        show={(
                                            <div className="spacer-bottom">
                                                <h3>Uploaded Attachments</h3>
                                                <MediaList
                                                    loading={isLoading}
                                                    media={attachments}
                                                    allowAddMedia={false}
                                                    showMediaCounter={false}
                                                    showPlaceholder={false}
                                                    showFilenames
                                                    size="large"
                                                    allowZoom
                                                />
                                            </div>
                                        )}
                                    />
                                    <div className="spacer-bottom">
                                        <h3>Notes</h3>
                                        <NotesThread
                                            id="pcn-notes-thread"
                                            notes={noteThreadItems}
                                            loading={isLoading}
                                            saving={savingNote}
                                            newNoteValue={newNoteDraft}
                                            onNewNoteChange={setNewNoteDraft}
                                            onAddNote={onAddNote}
                                            onEditNote={onEditNote}
                                            onDeleteNote={onDeleteNote}
                                        />
                                    </div>

                                    <Modal
                                        isOpen={showNewAssetModal}
                                        title="Add New Asset"
                                        onClose={onNewAssetClose}
                                        size={ModalSize.MEDIUM}
                                        clickOutsideClose={false}
                                    >

                                        <div className="bp6-dialog-body">
                                            <div className="spacer-bottom">
                                                <GridLayout gap={40} numberOfColumns={2} className={classNames({ "bp6-skeleton": isLoading })}>
                                                    <DataDisplayRecord header="Registration of New Vehicle" data={coreDetails.registration} />
                                                    <DataDisplayRecord header="Account of New Vehicle" data={coreDetails.account} />
                                                </GridLayout>
                                            </div>
                                            <GridLayout gap={40} numberOfColumns={2}>
                                                <div>
                                                    <FormSelect
                                                        headingText="Asset Type"
                                                        placeholder="Select Asset Type"
                                                        items={assetTypes}
                                                        onItemSelect={onAssetTypeSelect}
                                                        selectedValue={selectedAssetType?.id}
                                                    />
                                                </div>
                                                <div>
                                                    <FormSuggest
                                                        items={locationList}
                                                        onItemSelect={onLocationSelect}
                                                        placeholder="Search for location"
                                                        headingText="Select Location"
                                                        selectedValue={selectedLocation?.id}
                                                        onFilter={searchForLocation}
                                                        debounceTime={500}
                                                    />
                                                </div>
                                            </GridLayout>



                                        </div>

                                        <div className="bp6-dialog-footer">
                                            <div className="bp6-dialog-footer-actions">
                                                <Button text="Cancel" intent="danger" disabled={savingNewAsset} onClick={onNewAssetClose}></Button>
                                                <ButtonSave
                                                    id="pcn-new-asset-save"
                                                    onClick={onCreateNewAsset}
                                                    disabled={savingNewAsset}
                                                    simpleDisabled={selectedLocation == null || selectedAssetType == null}
                                                ></ButtonSave>
                                            </div>
                                        </div>

                                    </Modal>

                                    <AlertConfirm
                                        isOpen={confirmingDelete}
                                        onConfirm={onDeletePCN}
                                        onCancel={() => setConfirmingDelete(false)}
                                        title="Confirm Delete"
                                        confirmButtonText="Delete"
                                        confirmButtonIntent={Intent.DANGER}
                                    >
                                        Are you sure you want to delete this PCN?
                                    </AlertConfirm>

                                    <AlertUnsavedData
                                        isDirty={(isDataDirty || filesToUpload.length > 0 || newNoteDraft.length > 0) && !confirmingDelete}>
                                    </AlertUnsavedData>
                                </>
                            )}
                        />}
                        hide={<>
                            <NotificationInline
                                allowClose={false}
                                show
                                text="PCN does not exist or is no longer on hold"
                                intent="danger">
                            </NotificationInline>
                            <div>
                                <LinkButton intent="primary" text="Back to on hold listing" href="/pcn-admin/on-hold" id="return-pcn-on-hold-listing" />
                            </div>
                        </>}
                    />

                </div >
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );

}